import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, ChevronRight, Gift, Clock, Star, Sparkles, MapPin, Calendar, ArrowRight } from "lucide-react";

const MiniLogo = ({ name, size = "md" }) => {
  const colors = ["bg-sky-400", "bg-purple-400", "bg-green-400", "bg-orange-400", "bg-pink-400", "bg-indigo-400", "bg-teal-400", "bg-red-400"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const s = size === "sm" ? "w-9 h-9 text-xs" : size === "lg" ? "w-14 h-14 text-xl" : "w-11 h-11 text-sm";
  return <div className={`${s} rounded-2xl ${colors[i]} flex items-center justify-center flex-shrink-0`}><span className="text-white font-bold">{name?.charAt(0)?.toUpperCase()}</span></div>;
};

const ActionTile = ({ icon: Icon, label, count, color, onClick, testId }) => (
  <motion.button
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className={`${color} rounded-2xl p-4 text-left shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-white/60 hover:shadow-md transition-shadow`}
    data-testid={testId}
  >
    <Icon className="w-5 h-5 mb-3 opacity-70" />
    <p className="text-2xl font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>{count}</p>
    <p className="text-xs font-medium opacity-70 mt-0.5">{label}</p>
  </motion.button>
);

export const HubView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, expiringSoon, bestValue, stats, favoriteIds, toggleFavorite } = data;
  const totalValue = offers.reduce((s, o) => s + (o.offer_value_est || 0), 0);
  const [showAllBest, setShowAllBest] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-slate-50 pb-28" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Welcome hero */}
      <div className="px-6 pt-8 pb-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-slate-400 text-sm">Welcome back,</p>
          <h1 className="text-3xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {user?.first_name}
          </h1>
        </motion.div>

        {/* Birthday countdown card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mt-6 bg-gradient-to-br from-sky-500 to-indigo-500 rounded-3xl p-6 text-white relative overflow-hidden"
        >
          <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full" />
          <div className="absolute bottom-2 right-6 w-16 h-16 bg-white/5 rounded-full" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-sky-200" />
              <p className="text-sky-200 text-xs font-medium uppercase tracking-wider">Your Birthday</p>
            </div>
            <div className="flex items-end justify-between mt-2">
              <div>
                <p className="text-5xl font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>{stats.days_until_birthday}</p>
                <p className="text-sky-200 text-sm mt-1">days to go</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>${totalValue.toFixed(0)}</p>
                <p className="text-sky-200 text-sm">in free stuff</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Action tiles — the "hub" */}
      <div className="px-6 mb-6">
        <div className="grid grid-cols-2 gap-3">
          <ActionTile icon={Gift} label="Available" count={offers.length} color="bg-white text-slate-800" testId="hub-available" />
          <ActionTile icon={Clock} label="Expiring Soon" count={expiringSoon.length} color="bg-red-50 text-red-700" testId="hub-expiring" />
          <ActionTile icon={Star} label="Best Picks" count={bestValue.length} color="bg-amber-50 text-amber-700" testId="hub-best" />
          <ActionTile icon={Heart} label="Saved" count={favoriteIds.size} color="bg-pink-50 text-pink-700" testId="hub-saved" />
        </div>
      </div>

      {/* Priority: Expiring soon */}
      {expiringSoon.length > 0 && (
        <div className="px-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Act Now</h2>
            <span className="text-xs text-red-500 font-medium">{expiringSoon.length} expiring</span>
          </div>
          {expiringSoon.slice(0, 3).map((offer, i) => (
            <motion.div
              key={offer.business_id}
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.08 }}
              onClick={() => onOfferClick(offer)}
              className="flex items-center gap-3 py-3 cursor-pointer group border-b border-slate-100 last:border-0"
              data-testid={`hub-exp-${offer.business_id}`}
            >
              <MiniLogo name={offer.name} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm">{offer.name}</p>
                {getStatus(offer.business_id) && <UnifiedStatusBadge status={getStatus(offer.business_id)} />}
                <p className="text-xs text-slate-400 truncate">{offer.offer_title}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-green-600">${offer.offer_value_est}</span>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-sky-500 transition-colors" />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Top value picks — horizontal scroll */}
      {bestValue.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between px-6 mb-3">
            <h2 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Best Value</h2>
            <button onClick={() => setShowAllBest(!showAllBest)} className="text-xs text-sky-500 font-medium" data-testid="hub-see-all">
              {showAllBest ? "Collapse" : "See All"}
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto px-6 pb-2 scrollbar-hide">
            {(showAllBest ? bestValue : bestValue.slice(0, 6)).map(offer => (
              <motion.div
                key={offer.business_id}
                whileTap={{ scale: 0.95 }}
                onClick={() => onOfferClick(offer)}
                className="flex-shrink-0 w-32 bg-white rounded-2xl p-3 border border-slate-100 shadow-sm cursor-pointer hover:shadow-md transition-all"
                data-testid={`hub-best-${offer.business_id}`}
              >
                <MiniLogo name={offer.name} size="sm" />
                <p className="text-xs font-semibold text-slate-800 mt-2 truncate">{offer.name}</p>
                <p className="text-sm font-bold text-green-600">${offer.offer_value_est}</p>
                <p className="text-[10px] text-slate-400">{offer.category}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Browse all — compact list preview */}
      <div className="px-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>All Birthday Deals</h2>
          <span className="text-xs text-slate-400">{offers.length} total</span>
        </div>
        <div className="space-y-1">
          {offers.slice(0, 8).map(offer => (
            <div
              key={offer.business_id}
              onClick={() => onOfferClick(offer)}
              className="flex items-center gap-3 py-2.5 px-2 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors"
              data-testid={`hub-offer-${offer.business_id}`}
            >
              <MiniLogo name={offer.name} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{offer.name}</p>
                <p className="text-xs text-slate-400">{offer.category}</p>
              </div>
              <span className="text-sm font-bold text-green-600">${offer.offer_value_est}</span>
            </div>
          ))}
        </div>
        {offers.length > 8 && (
          <button className="w-full mt-3 py-3 bg-slate-100 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors flex items-center justify-center gap-2" data-testid="hub-browse-all">
            Browse All {offers.length} Deals <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
